import { useEffect, useState, useRef, memo, forwardRef, useImperativeHandle } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { cloneDeep, isNil, values, isEmpty } from 'lodash';
// Import Store
import { useAppDispatch } from 'store';
import { clearDynamicSegmentFilterList } from 'store/slices/dynamicSegment';
// Import UI Components
import { ModuleTitle, Breadcrumb, BusyLoader, Empty, Tag, Icon, ExtendedInput } from '@geneui/components';
// Import Components
import { UIHashComparisonContainer } from './UIHashContainers';
import DynamicSegmentList from './DynamicSegmentList';
import { FiltersContainer } from './FiltersContainer';
import DynamicSegmentFilterFormWrapper from './DynamicSegmentFilterFormWrapper';
// Import Services
import { getByHashKey, getInitialList } from './service';
// Import Constants
import { l } from 'constants/common';
import { objectTypesMap } from './constants';
// Import Styles
import 'assets/scss/dynamicSegment.scss';

interface NavigationItem {
    title: string;
    slug: Nilable<string>;
}

interface Props {
    queryConfigurations?: AnyObject;
    columnsReport?: AnyObject[];
    pageType?: string;
    tabActiveId: string;
    setTabActiveId: (value: string) => void;
    unifiedCurrency?: Nullable<string>;
    hideFilterContainerTabs?: boolean;
    isExclusionRules?: boolean;
}

export interface DynamicSegmentRef {
    reset: () => void;
}

const DynamicSegment = forwardRef<DynamicSegmentRef, Props>((props, ref) => {
    const {
        queryConfigurations,
        columnsReport,
        pageType,
        tabActiveId,
        setTabActiveId,
        unifiedCurrency,
        hideFilterContainerTabs = false,
        isExclusionRules = false,
    } = props;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [activeList, setActiveList] = useState<UIHashComparisonContainer[][]>([]);
    const [editingFilter, setEditingFilter] = useState(null);
    const [navigationList, setNavigationList] = useState<NavigationItem[]>([{ title: 'Filters', slug: null }]);
    const [filterableList, setFilterableList] = useState<(UIHashComparisonContainer | UIHashComparisonContainer[])[]>(
        [],
    );
    const [filteredList, setFilteredList] = useState<UIHashComparisonContainer[][]>([]);
    const [searchInputValue, setSearchInputValue] = useState('');
    const [searchedValue, setSearchedValue] = useState('');
    const [searchableFilterList, setSearchableFilterList] = useState([]);
    const [readOnlyFilterContainer, setReadOnlyFilterContainer] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    let list: UIHashComparisonContainer[][] = [];
    let navigationListContainer: NavigationItem[] = [];

    /** Making search input focused on mount **/
    useEffect(() => inputRef.current?.focus?.(), [filterableList]);

    /** Initializing filters list and setting activeList and filterableList state on mount **/
    useEffect(() => {
        setInitialList();
        if (!isEmpty(queryConfigurations)) {
            const filtered = Object.keys(queryConfigurations)
                .filter((key) => key.includes('categoriesFilters') && !key.includes('root'))
                .reduce((obj, key) => {
                    obj[key] = queryConfigurations[key];
                    return obj;
                }, {});
            let resultSearchedFilterList = [];
            values(filtered).forEach((filterObj: AnyObject) => {
                resultSearchedFilterList = resultSearchedFilterList.concat(
                    filterObj.nextItems.filter((nextItem) => !nextItem.hashKey.includes('categoriesFilters')),
                );
            });
            setSearchableFilterList(resultSearchedFilterList);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryConfigurations]);

    useImperativeHandle(ref, () => ({
        reset() {
            resetActiveList();
            setSearchedValue('');
            setEditingFilter(null);
        },
    }));

    /** Initializing filters list **/
    const initializeFiltersList = () => {
        const initialList = getInitialList(queryConfigurations);
        return initialList ? initialList.nextItems : [];
    };

    const setInitialList = () => {
        const initialList = initializeFiltersList();
        setActiveList([initialList]);
        setFilterableList([initialList]);
        // if (!isEmpty(initialList)) { TODO: in future, here we can trigger a filter to be opened during init
        //     handleChangeActiveList(`${objectTypesMap.categoriesFilters}2`);
        // }
    };

    const resetActiveList = () => {
        const initialList = initializeFiltersList();
        setActiveList([initialList]);
        setFilterableList([initialList]);
        setNavigationList([{ title: t(l.Filters), slug: null }]);
        navigationListContainer = [];
        list = [];
    };

    /** Handling click on active filter list item **/
    const handleChangeActiveList = (
        hashKey: string,
        displayName: Nilable<string> = undefined,
        id: Nilable<string> = null,
    ) => {
        const item = cloneDeep(getByHashKey(hashKey, queryConfigurations));
        !isNil(id) && resetActiveList();
        searchInputValue && setSearchedValue(searchInputValue);
        setSearchInputValue('');
        navigationListContainer.push({
            title: displayName || item.parentDisplayName,
            slug: hashKey,
        });

        if (item.parentHashKey === `${objectTypesMap.categoriesFilters}root`) {
            list.push(item.nextItems);
            list.push(getInitialList(queryConfigurations).nextItems);

            setActiveList(list.reverse());
            setFilterableList(list);

            if (searchInputValue && list.length === 2) {
                list[0] = findFilter(list[0], searchInputValue);
                setFilteredList(list);
                setFilterableList(list);
            } else if (searchInputValue && list.length === 3) {
                list[1] = findFilter(list[1], searchInputValue);

                setFilteredList(list);
                setFilterableList(list);
            }
            navigationListContainer.push({ title: t(l.Filters), slug: null });
            setNavigationList(navigationListContainer.reverse());
            list = [];
        } else if (!item.nextItems) {
            !isNil(id) ? (item.id = id) : delete item.id;
            list.push(item);
            handleChangeActiveList(item.parentHashKey);
        } else if (item && item.nextItems) {
            list.push(item.nextItems);
            handleChangeActiveList(item.parentHashKey);
        }
    };

    /** Bread crumbs click navigation handling **/
    const handleClickNavigation = ({ slug }) => {
        if (slug) {
            handleChangeActiveList(slug);
            // eslint-disable-next-line no-dupe-else-if
        } else if (slug && filteredList) {
            // TODO: check this case, seems unreachable
            setFilterableList([filteredList] as unknown as UIHashComparisonContainer[][]);
        } else {
            const initialList = initializeFiltersList();
            setActiveList([initialList]);
            setFilterableList([initialList]);
            setNavigationList([{ title: t(l.Filters), slug: null }]);
        }
    };

    /** Setting the search input value and calling the active list filter function **/
    const setInputValue = (event) => {
        const {
            target: { value },
        } = event;
        searchValueSetter(value);
    };

    /** Filtering the active list and changing it's state (filterableList) **/
    const handleChangeSearch = (value) => {
        const foundFilters = findFilter(activeList[activeList.length - 1], value);
        const resultFilteredList = [...filterableList];
        resultFilteredList[activeList.length - 1] = foundFilters;
        setFilterableList(resultFilteredList);
    };

    /** Filtering the active list array (filterableList) **/
    const findFilter = (list, value) => {
        return list && list[0] && value
            ? list.filter(({ displayName }) => {
                  return displayName.toLowerCase().includes(value.toLowerCase());
              })
            : undefined;
    };

    const getColumnType = (index) => {
        if (activeList[index + 1] instanceof UIHashComparisonContainer) return false;
        return activeList.length - index <= 2;
    };

    const tagDeleteHandler = (e) => {
        e.stopPropagation();
        setSearchedValue('');
    };

    const searchValueSetter = (value) => {
        const tmpValue = value.trimStart();
        if (tmpValue || searchInputValue) {
            setSearchInputValue(tmpValue);
            handleChangeSearch(tmpValue);
            if (tmpValue) {
                const searchedFilteredData = findFilter(searchableFilterList, tmpValue.trimEnd());
                const result = [isEmpty(searchedFilteredData) ? [null] : searchedFilteredData];

                setFilterableList(result);
                setActiveList(result);
            } else {
                setInitialList();
            }
        }
    };

    const tagClickHandler = () => {
        searchValueSetter(searchedValue);
    };

    const editModeCancelHandler = () => {
        resetActiveList();
        setEditingFilter(null);
    };

    const cleanUp = () => {
        return () => {
            dispatch(clearDynamicSegmentFilterList());
        };
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(cleanUp, []);

    return (
        <div className="create-dynamic-segment-p-w-content">
            <div className="create-dynamic-segment-p-w-c-i-column">
                <div className="create-dynamic-segment-p-w-c-i-column-head">
                    <ModuleTitle
                        title={<Breadcrumb data={navigationList} collapsed={false} onClick={handleClickNavigation} />}
                        description=" "
                    />
                    <div className="crm-dynamic-segment-header crm-full-width p-20 c-crm-s-header">
                        <ExtendedInput
                            ref={inputRef}
                            type="text"
                            canClear={true}
                            icon={'bc-icon-search'}
                            className="search-holder crm-content-width"
                            placeholder={`Search in`}
                            onChange={setInputValue}
                            value={searchInputValue}
                        />
                        {searchedValue && (
                            <Tag
                                appearance={'light'}
                                name={searchedValue}
                                size={'big'}
                                onClick={tagClickHandler}
                                icons={<Icon type="bc-icon-clear-small" onClick={tagDeleteHandler} />}
                            />
                        )}
                    </div>
                </div>
                <div className="create-dynamic-segment-p-w-c-i-column-content">
                    {activeList[0]?.length > 0 ? (
                        <>
                            <div
                                className={classNames('create-dynamic-segment-p-w-c-i-c-content-column', {
                                    'crm-editable-state': readOnlyFilterContainer,
                                })}
                            >
                                {filterableList.map((elem, index) => (
                                    <DynamicSegmentList
                                        setReadOnlyFilterContainer={setReadOnlyFilterContainer}
                                        key={index}
                                        handleChangeActiveColumnsList={handleChangeActiveList}
                                        list={elem}
                                        full={getColumnType(index)}
                                        selected={navigationList[index + 1] ? navigationList[index + 1].title : null}
                                    />
                                ))}
                            </div>

                            <div className="create-dynamic-segment-p-w-c-i-c-content-column">
                                {activeList.length < 2 && (
                                    <Empty
                                        appearance="greyscale"
                                        withImage
                                        title={t(l.SelectAFilter)}
                                        subTitle={t(l.SelectAFilterAndStartBuildingASegment)}
                                    />
                                )}
                                {filterableList[filterableList.length - 1] instanceof UIHashComparisonContainer && (
                                    <DynamicSegmentFilterFormWrapper
                                        data={filterableList[filterableList.length - 1] as UIHashComparisonContainer}
                                        setReadOnlyFilterContainer={setReadOnlyFilterContainer}
                                        editModeCancelHandler={editModeCancelHandler}
                                        listLength={activeList.length}
                                        tabActiveId={tabActiveId}
                                    />
                                )}
                            </div>
                        </>
                    ) : (
                        <BusyLoader
                            isBusy
                            type="spinner"
                            spinnerSize={'big'}
                            className="crm-full-width crm-justify-content-center"
                        />
                    )}
                </div>
            </div>
            <div className="create-dynamic-segment-p-w-c-i-column">
                <FiltersContainer
                    readOnly={readOnlyFilterContainer}
                    setReadOnly={setReadOnlyFilterContainer}
                    editingFilter={editingFilter}
                    setEditingFilter={setEditingFilter}
                    resetActiveList={resetActiveList}
                    handleChangeActiveList={handleChangeActiveList}
                    columnsReport={columnsReport}
                    pageType={pageType}
                    tabActiveId={tabActiveId}
                    setTabActiveId={setTabActiveId}
                    editModeCancelHandler={editModeCancelHandler}
                    unifiedCurrency={unifiedCurrency}
                    hideFilterContainerTabs={hideFilterContainerTabs}
                    isExclusionRules={isExclusionRules}
                />
            </div>
        </div>
    );
});

DynamicSegment.displayName = 'DynamicSegment';

export default memo(DynamicSegment);
